import type { FC } from 'react';
import cx from 'classnames';
import { useRouter } from 'next/router';

import cypressIds from 'Components/cypress_ids.json';
import { useBreakpoint } from 'Context/BreakpointsContext/BreakpointsContext';
import { SellerCommunicationsButton } from 'Features/SellerCommunications';
import { triggerAccountPageSearchInputEvent } from 'Services/analytics/events';
import { setAccountFilter } from 'Stores/AccountFiltersStore/AccountFiltersStore.helpers';
import { isString } from 'Utilities/helpers';
import useKeywordSearch from 'Utilities/hooks/useKeywordSearch';

import { DEFAULT_VEHICLES_PAGE_SIZE } from '../AccountPage.consts';
import type { VehicleListQuery } from '../AccountPage.types';
import { isActivePage, isCompletedPage, isUnderOfferPage } from '../Purchases/Purchases.helpers';
import { PurchasesDownloadCsv } from '../Purchases/PurchasesDownloadCsv/PurchasesDownloadCsv';
import useAccountRouter from '../useAccountRouter';

import { Menu } from './components/AccountSidebarDrawer/AccountSidebarDrawer';
import { FiltersPopover } from './components/FiltersPopover/FiltersPopover';
import { Pagination } from './components/Pagination/Pagination';
import { Search } from './components/Search/Search';
import { Status } from './components/Status/Status';

import styles from './Filters.module.scss';

type FiltersProps = { gridClass: string } & VehicleListQuery;

const Filters: FC<FiltersProps> = ({ data, gridClass, queryStatus }) => {
	const router = useRouter();

	const {
		minWidth: { breakpointDesktopAccount: isLargeDesktop },
	} = useBreakpoint();

	const { changePage, keyword, purchasesCompleteStatusType, slug } = useAccountRouter();

	// this needs to be declared here so it avoids rerendering on controlled input in the Search component
	const { onClear, onSearch } = useKeywordSearch({
		initialKeyword: keyword ? String(keyword) : '',
		searchFunction: ({ keyword: searchKeyword }) => {
			const trimmedSearchKeyword = isString(searchKeyword) ? searchKeyword?.trim() : searchKeyword;
			setAccountFilter(router, { keyword: trimmedSearchKeyword });
			triggerAccountPageSearchInputEvent({ searchTerm: trimmedSearchKeyword, slug });
		},
	});

	const showMenu = !isLargeDesktop;
	const showStatus = isCompletedPage(slug);
	const showDownload = isCompletedPage(slug);
	const showExtraFilters = !(isActivePage(slug) || isUnderOfferPage(slug));

	return (
		<div className={styles.filtersWrapper}>
			<div className={cx(styles.filters, gridClass)}>
				{showMenu && <Menu />}
				<Search keyword={keyword} onClear={onClear} onSearch={onSearch} slug={slug} />
				{showExtraFilters && <FiltersPopover />}
				{showDownload && <PurchasesDownloadCsv isDisabled={!data?.count} queryStatus={queryStatus} />}
				{showStatus && (
					<div className={styles.sellerCommunications}>
						<SellerCommunicationsButton />
					</div>
				)}
				<Pagination
					cypressId={cypressIds.accountFilters.pagination}
					queryStatus={queryStatus}
					recordsPerPage={DEFAULT_VEHICLES_PAGE_SIZE}
					totalRecords={data?.count}
				/>
				{showStatus && (
					<Status changePage={changePage} purchasesCompleteStatusType={purchasesCompleteStatusType} router={router} />
				)}
			</div>
		</div>
	);
};

export { Filters };
